import { useState } from "react";
import { toast } from "sonner";
import {
  Youtube, Globe, History as HistoryIcon, Settings as SettingsIcon, UserRound,
  ListVideo, HardDrive, Images, Disc3, Mic2, Heart, Radio, ExternalLink, Play, Lock,
  Tv, AlertTriangle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

// type: embed-search | embed-paste | external | web | util | soon
const TILES = [
  { key: "youtube", name: "YouTube", type: "embed-search", accent: "#00D9FF", mono: "YT" },
  { key: "netflix", name: "Netflix", type: "external", accent: "#FF0040", mono: "N", url: "https://www.netflix.com" },
  { key: "disney", name: "Disney+", type: "external", accent: "#9D4EDD", mono: "D+", url: "https://www.disneyplus.com" },
  { key: "prime", name: "Prime Video", type: "external", accent: "#FF006E", mono: "pv", url: "https://www.primevideo.com" },

  { key: "crunchyroll", name: "Crunchyroll", type: "external", accent: "#FF7A00", mono: "cr", url: "https://www.crunchyroll.com" },
  { key: "max", name: "Max", type: "external", accent: "#00D9FF", mono: "MAX", url: "https://www.max.com" },
  { key: "tubi", name: "Tubi", type: "embed-paste", accent: "#FF006E", mono: "tubi", url: "https://tubitv.com", free: true },
  { key: "pluto", name: "Pluto TV", type: "embed-paste", accent: "#9D4EDD", mono: "pluto", url: "https://pluto.tv", free: true },

  { key: "twitch", name: "Twitch", type: "external", accent: "#9146FF", mono: "tw", url: "https://www.twitch.tv" },
  { key: "hulu", name: "Hulu", type: "external", accent: "#00FF66", mono: "hulu", url: "https://www.hulu.com" },
  { key: "peacock", name: "Peacock", type: "external", accent: "#00D9FF", mono: "P", url: "https://www.peacocktv.com" },
  { key: "appletv", name: "Apple TV+", type: "external", accent: "#C9D6E5", mono: "tv+", url: "https://tv.apple.com" },

  { key: "youtube_live", name: "YouTube Live", type: "embed-search", accent: "#FF0040", icon: Radio },
  { key: "m3u", name: "M3U Playlist", type: "m3u", accent: "#00FF66", icon: Tv },
  { key: "playlist", name: "Playlist", type: "soon", accent: "#9D4EDD", icon: ListVideo },
  { key: "drive", name: "Drive", type: "soon", accent: "#00FF66", icon: HardDrive },
  { key: "photos", name: "Photos", type: "soon", accent: "#FF006E", icon: Images },

  { key: "web", name: "Web", type: "web", accent: "#00D9FF", icon: Globe },
  { key: "ravedj", name: "Rave DJ", type: "soon", accent: "#FF006E", icon: Disc3 },
  { key: "karaoke", name: "Karaoke", type: "soon", accent: "#9D4EDD", icon: Mic2 },
  { key: "likes", name: "Likes", type: "soon", accent: "#FF0040", icon: Heart },
];

const BOTTOM = [
  { key: "history", name: "History", accent: "#00D9FF", icon: HistoryIcon },
  { key: "settings", name: "Settings", accent: "#88A0C0", icon: SettingsIcon },
  { key: "profile", name: "Profile", accent: "#00FF66", icon: UserRound },
];

function Glyph({ t, size = 46 }) {
  const wide = t.mono && t.mono.length > 2;
  if (t.icon) {
    const I = t.icon;
    return (
      <span className="svc-glyph flex items-center justify-center rounded-xl" style={{ width: size, height: size, background: `${t.accent}14`, border: `1px solid ${t.accent}55` }}>
        <I className="w-5 h-5" style={{ color: t.accent, filter: `drop-shadow(0 0 6px ${t.accent})` }} />
      </span>
    );
  }
  return (
    <span
      className="svc-glyph flex items-center justify-center rounded-xl font-display font-black"
      style={{
        width: size, height: size, background: `${t.accent}14`, color: t.accent,
        fontSize: wide ? 13 : 22, border: `1px solid ${t.accent}55`,
        textShadow: `0 0 10px ${t.accent}, 0 0 22px ${t.accent}88`,
      }}
    >
      {t.mono}
    </span>
  );
}

function Tile({ t, onClick }) {
  const soon = t.type === "soon";
  return (
    <button
      data-testid={`svc-tile-${t.key}`}
      onClick={() => onClick(t)}
      className={`svc-tile group relative flex flex-col items-center justify-center gap-2 rounded-2xl p-3 bg-[#0A0E27]/70 backdrop-blur-md ${soon ? "opacity-60" : ""}`}
      style={{ border: `1px solid ${t.accent}55`, boxShadow: `0 0 14px ${t.accent}22` }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 30px ${t.accent}88`; e.currentTarget.style.borderColor = t.accent; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = `0 0 14px ${t.accent}22`; e.currentTarget.style.borderColor = `${t.accent}55`; }}
    >
      <Glyph t={t} />
      <span className="text-[11px] sm:text-xs font-display font-bold uppercase tracking-wide text-[#F0F6FF] text-center leading-tight">{t.name}</span>
      {t.free && (
        <span className="absolute top-1.5 right-1.5 text-[8px] font-bold uppercase px-1 py-0.5 rounded" style={{ background: "rgba(0,255,102,.14)", color: "#00FF66", border: "1px solid rgba(0,255,102,.4)" }}>Free</span>
      )}
      {soon && (
        <span className="absolute top-1.5 right-1.5 text-[8px] font-bold uppercase px-1 py-0.5 rounded bg-white/10 text-[#88A0C0] border border-white/15">Soon</span>
      )}
    </button>
  );
}

export default function ServiceGrid({ onLaunch, onOpenFriends, onOpenAuth, account }) {
  const [modal, setModal] = useState(null); // { t, kind: 'paste' | 'external' }
  const [url, setUrl] = useState("");

  const openModal = (t, kind) => { setModal({ t, kind }); setUrl(kind === "paste" && t.free ? t.url : ""); };
  const close = () => setModal(null);

  const handle = (t) => {
    switch (t.type) {
      case "embed-search":
        onLaunch(`?search=1`);
        break;
      case "embed-paste":
        openModal(t, "paste");
        break;
      case "web":
        openModal(t, "paste");
        break;
      case "m3u":
        if (!account) { toast("Log in to load M3U playlists & save them"); onOpenAuth(); break; }
        openModal(t, "m3u");
        break;
      case "external":
        openModal(t, "external");
        break;
      case "soon":
        toast(`✨ ${t.name} is coming soon`, { description: "This one needs its own integration — tell me to build it next!" });
        break;
      case "util":
        if (t.key === "history") { account ? onOpenFriends("history") : (toast("Log in to see your watch history"), onOpenAuth()); }
        else if (t.key === "profile") { account ? onOpenFriends("friends") : onOpenAuth(); }
        else if (t.key === "settings") { toast("⚙️ Chat & sync settings live inside a room", { description: "Open the notifications menu in any watch room." }); }
        break;
      default:
        break;
    }
  };

  const startPaste = () => {
    let u = (url || modal.t.url || "").trim();
    if (!u) { toast.error("Paste a video link first"); return; }
    if (!/^https?:\/\//i.test(u)) u = "https://" + u;
    close();
    onLaunch(`?v=${encodeURIComponent(u)}`);
  };

  const startExternal = () => {
    if (modal.t.url) window.open(modal.t.url, "_blank", "noopener");
    close();
    onLaunch(``); // empty room; driver uses SYNC countdown
  };

  const startM3U = () => {
    const u = (url || "").trim();
    close();
    // open a room in Live-TV mode; pass the playlist so the panel auto-loads it
    onLaunch(u ? `?tv=1&m3u=${encodeURIComponent(/^https?:\/\//i.test(u) ? u : "https://" + u)}` : `?tv=1`);
  };

  const t = modal?.t;

  return (
    <div className="mt-8 anim-in" data-testid="service-grid" style={{ animationDelay: "0.15s" }}>
      <div className="flex items-center gap-2 mb-3">
        <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[#00D9FF]/40" />
        <p className="text-xs font-display font-bold uppercase tracking-widest neon-cyan">Pick a service</p>
        <span className="h-px flex-1 bg-gradient-to-l from-transparent to-[#00D9FF]/40" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {TILES.map((tile) => <Tile key={tile.key} t={tile} onClick={handle} />)}
      </div>

      <div className="grid grid-cols-3 gap-2.5 mt-2.5">
        {BOTTOM.map((tile) => <Tile key={tile.key} t={{ ...tile, type: "util" }} onClick={handle} />)}
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="glass-cyber border-0 text-[#F0F6FF]" data-testid="svc-modal">
          {t && modal.kind === "paste" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-display text-xl uppercase tracking-wide">
                  <Glyph t={t} size={30} /> {t.name}
                </DialogTitle>
                <DialogDescription className="text-[#88A0C0] pt-1">
                  {t.key === "web"
                    ? "Paste any video link (YouTube, Tubi, Pluto, an .mp4/.m3u8, or an embeddable page) and watch it together in sync."
                    : `${t.name} loads right inside Ibeflix. Paste a ${t.name} video URL${t.url ? " (or use the homepage)" : ""} to start a synced room.`}
                </DialogDescription>
              </DialogHeader>
              {t.url && t.key !== "web" && (
                <a href={t.url} target="_blank" rel="noopener noreferrer" data-testid="svc-open-tab" className="btn-neon flex items-center justify-center gap-2 h-11 rounded-full bg-white/5 border border-[#00D9FF]/30 text-[#F0F6FF] font-semibold hover:border-[#00D9FF] hover:text-[#00D9FF] transition-colors">
                  <ExternalLink className="w-4 h-4" /> Browse {t.name}
                </a>
              )}
              <input
                data-testid="svc-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") startPaste(); }}
                placeholder={`Paste the ${t.name} video URL…`}
                className="input-neon w-full h-11 bg-[#060817]/80 border border-[#00D9FF]/25 rounded-full px-4 text-sm text-[#F0F6FF] outline-none transition-colors"
              />
              <button data-testid="svc-paste-start" onClick={startPaste} className="btn-neon glow-cyan w-full h-11 rounded-full bg-[#00D9FF] text-navy-ink font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2">
                <Play className="w-4 h-4 fill-current" /> Watch Together
              </button>
            </>
          )}

          {t && modal.kind === "external" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-display text-xl uppercase tracking-wide">
                  <Glyph t={t} size={30} /> Watch {t.name} Together
                </DialogTitle>
                <DialogDescription className="text-[#88A0C0] pt-1">
                  {t.name} blocks in-app embedding, so you play it in your own tab and Ibeflix keeps everyone in sync.
                </DialogDescription>
              </DialogHeader>
              <ol className="space-y-2 text-sm text-[#F0F6FF]">
                {[
                  `Open ${t.name} in a new tab`,
                  "Find your show and get it ready to play",
                  "Come back to Ibeflix and press SYNC",
                  "Countdown 3… 2… 1… PLAY 🎬 — everyone hits play together",
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="w-5 h-5 shrink-0 rounded-full bg-[#00D9FF]/15 border border-[#00D9FF]/50 text-[#00D9FF] text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              <div className="space-y-2 pt-1">
                <a href={t.url} target="_blank" rel="noopener noreferrer" data-testid="svc-open-external" className="btn-neon flex items-center justify-center gap-2 h-11 rounded-full bg-white/5 border border-[#00D9FF]/30 text-[#F0F6FF] font-semibold hover:border-[#00D9FF] hover:text-[#00D9FF] transition-colors">
                  <ExternalLink className="w-4 h-4" /> Open {t.name} →
                </a>
                <button data-testid="svc-external-start" onClick={startExternal} className="btn-neon glow-pink w-full h-11 rounded-full bg-[#FF006E] text-white font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2">
                  <Lock className="w-4 h-4" /> Create Room & SYNC
                </button>
              </div>
            </>
          )}

          {t && modal.kind === "m3u" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-display text-xl uppercase tracking-wide">
                  <Glyph t={t} size={30} /> M3U Playlist
                </DialogTitle>
                <DialogDescription className="text-[#88A0C0] pt-1">
                  Paste an M3U / M3U8 playlist URL to watch live TV together. You'll pick a channel inside the room and everyone syncs on 3… 2… 1… PLAY.
                </DialogDescription>
              </DialogHeader>
              <div data-testid="m3u-legal-warning" className="flex items-start gap-2.5 rounded-xl p-3 text-[13px] leading-snug" style={{ background: "rgba(230,164,41,.1)", border: "1px solid rgba(230,164,41,.4)", color: "#F0D48A" }}>
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#E6A429" }} />
                <span><strong className="text-[#E6A429]">Only legal streams please.</strong> Using pirated IPTV is illegal. Make sure your M3U link is legal — e.g. Pluto TV, free/public broadcasts, or your own hosted streams.</span>
              </div>
              <input
                data-testid="m3u-grid-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") startM3U(); }}
                placeholder="https://example.com/playlist.m3u"
                className="input-neon w-full h-11 bg-[#060817]/80 border border-[#00FF66]/25 rounded-full px-4 text-sm text-[#F0F6FF] outline-none transition-colors"
              />
              <button data-testid="m3u-grid-start" onClick={startM3U} className="btn-neon glow-cyan w-full h-11 rounded-full bg-[#00FF66] text-navy-ink font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2">
                <Tv className="w-4 h-4" /> Load Playlist & Watch
              </button>
              <p className="text-[11px] text-[#71717A] text-center">Tip: leave it blank to open the Live TV picker and load a saved playlist.</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
